/**
 * A school admin's orders for named roster students, paid by the school
 * (`/api/school-student-orders`, SCHOOL_FOR_STUDENT). Replaces the stand-in
 * school households that held second copies of students.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiFetch } from '@/api/http'
import type { Mealtime } from '@/lib/mealtime'

export interface SchoolStudentOrderWire {
  id: string
  serviceDate: string
  mealtime: Mealtime
  status: string
  student: { id: string; name: string }
  items: Array<{ name: string; quantity: number }>
  /** What the school's account paid, after the student's meal-plan share. */
  schoolCents: number
  /** What the student's meal plan covered. */
  sponsoredCents: number
  canCancel: boolean
}

export interface PlaceSchoolStudentOrderVars {
  locationId: string
  studentUserId: string
  serviceDate: string
  mealtime: Mealtime
  /** The meal and the price shown for it; left out of an à la carte-only order. */
  menuItemId?: string
  expectedUnitPriceCents?: number
  /** À la carte items; left out when there are none. */
  alaCarte?: Array<{ menuItemId: string; quantity: number; expectedUnitPriceCents: number }>
  idempotencyKey: string
}

export function schoolStudentOrdersQueryKey(args: { locationId: string; from: string; to: string }) {
  return ['school-student-orders', args.locationId, args.from, args.to] as const
}

/** The school's student orders in [from, to) — `to` is exclusive. */
export function useSchoolStudentOrdersQuery(args: { locationId: string; from: string; to: string; enabled: boolean }) {
  return useQuery({
    queryKey: schoolStudentOrdersQueryKey(args),
    queryFn: ({ signal }) =>
      apiFetch<{ orders: SchoolStudentOrderWire[] }>('/api/school-student-orders', {
        query: { locationId: args.locationId, from: args.from, to: args.to },
        signal,
      }),
    enabled: args.enabled && args.locationId.length > 0,
    staleTime: 30_000,
  })
}

export function usePlaceSchoolStudentOrderMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: PlaceSchoolStudentOrderVars) =>
      apiFetch<{ batchId: string; status: string; totalCents: number }>('/api/school-student-orders', {
        method: 'POST',
        body: vars,
      }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['school-student-orders'] }),
  })
}

export function useCancelSchoolStudentOrderMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { locationId: string; batchId: string }) =>
      apiFetch<{ batchId: string; status: 'VOIDED' }>(
        `/api/school-student-orders/${encodeURIComponent(vars.batchId)}/cancel`,
        { method: 'POST', body: { locationId: vars.locationId } },
      ),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['school-student-orders'] }),
  })
}
